import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Progress } from '../../../ui/progress';
import { MetricCard, MetricLabel, MetricValue } from '../../../ui/metric-card';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Mail, Loader2, Users, Clock, TrendingUp, Info } from 'lucide-react';
import { prettyDate } from '../surveyUiUtils';

const isCompleted = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'completed' || s === 'submitted';
};

const DashboardTab = ({ campaign, invitationRows = [], facilities = [], onRemindClick, loading }) => {
  const isClosing = String(campaign?.status || '').toLowerCase() === 'closing';
  const isClosed = String(campaign?.status || '').toLowerCase() === 'closed';
  const isActive = String(campaign?.status || '').toLowerCase() === 'active';

  const invitations = campaign?.invitations || [];
  const invited = invitations.filter((inv) => String(inv.status || '').toLowerCase() !== 'revoked').length;
  const completed = invitations.filter((inv) => isCompleted(inv.status)).length;
  const outstanding = Math.max(0, invited - completed);
  const responseRate = invited > 0 ? Math.round((completed / invited) * 100) : 0;

  const deadlineRaw = campaign?.deadlineDate || campaign?.deadline;
  const daysRemaining = useMemo(() => {
    if (!deadlineRaw) return null;
    const d = new Date(String(deadlineRaw).trim());
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }, [deadlineRaw]);

  const locationStats = useMemo(() => {
    const byName = new Map();

    facilities.forEach((f) => {
      if (f.name) {
        byName.set(f.name, { name: f.name, fte: Number(f.fte || 0), completed: 0, total: 0 });
      }
    });

    invitationRows.forEach((row) => {
      const name = row.locationName || null;
      if (!name) return;
      if (!byName.has(name)) {
        byName.set(name, { name, fte: 0, completed: 0, total: 0 });
      }
      const entry = byName.get(name);
      entry.total += 1;
      if (isCompleted(row.status)) entry.completed += 1;
    });

    return Array.from(byName.values()).filter((e) => e.total > 0 || e.fte > 0);
  }, [facilities, invitationRows]);

  const hasLocationData = locationStats.length > 0;

  return (
    <div className='space-y-6'>
      {isClosing && (
        <Alert>
          <Loader2 size={14} strokeWidth={1.5} className='animate-spin' />
          <AlertTitle>Calculating results</AlertTitle>
          <AlertDescription>
            Applying emission factors to{' '}
            {completed} respondent{completed !== 1 ? 's' : ''}{' '}
            {hasLocationData ? `across ${locationStats.length} location${locationStats.length !== 1 ? 's' : ''}` : ''}.
            This usually takes under 2 minutes.
          </AlertDescription>
        </Alert>
      )}

      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        <MetricCard accent='default'>
          <MetricLabel>Response rate</MetricLabel>
          <MetricValue unit='%'>{responseRate}</MetricValue>
          <div className='mt-3'>
            <Progress value={responseRate} />
          </div>
          <div className='text-xs text-muted-foreground mt-2'>
            {completed} of {invited} completed
          </div>
        </MetricCard>

        <MetricCard accent='success'>
          <MetricLabel>Completed</MetricLabel>
          <MetricValue>{completed}</MetricValue>
          <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
            <TrendingUp size={12} strokeWidth={1.5} />
            responses received
          </div>
        </MetricCard>

        <MetricCard accent='amber'>
          <MetricLabel>Outstanding</MetricLabel>
          <MetricValue>{outstanding}</MetricValue>
          <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
            <Users size={12} strokeWidth={1.5} />
            eligible for reminders
          </div>
        </MetricCard>

        <MetricCard>
          <MetricLabel>Deadline</MetricLabel>
          <MetricValue>{daysRemaining !== null ? daysRemaining : '—'}</MetricValue>
          <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
            <Clock size={12} strokeWidth={1.5} />
            {daysRemaining !== null
              ? daysRemaining === 0
                ? 'due today'
                : 'days remaining'
              : prettyDate(deadlineRaw) !== '—'
              ? prettyDate(deadlineRaw)
              : 'no deadline set'}
          </div>
        </MetricCard>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6'>
        <Card>
          <CardHeader className='flex-row items-center justify-between space-y-0'>
            <div>
              <CardTitle className='text-base'>Response rate by office location</CardTitle>
              <p className='text-xs text-muted-foreground mt-1'>
                Each location is extrapolated independently to its own FTE headcount.
              </p>
            </div>
            {!isClosing && !isClosed && onRemindClick && (
              <Button size='sm' variant='outline' onClick={onRemindClick} loading={Boolean(loading?.remind)}>
                <Mail size={14} strokeWidth={1.5} />
                Send reminders
              </Button>
            )}
          </CardHeader>
          <CardContent className='space-y-5'>
            {hasLocationData ? (
              locationStats.map((row) => {
                const denominator = row.fte > 0 ? row.fte : row.total;
                const rate = denominator > 0 ? Math.round((row.completed / denominator) * 100) : 0;
                const outstanding = Math.max(0, (row.fte > 0 ? row.fte : row.total) - row.completed);
                return (
                  <div key={row.name}>
                    <div className='flex items-end justify-between mb-1.5'>
                      <div>
                        <div className='font-medium text-[14px]'>{row.name}</div>
                        <div className='text-xs text-muted-foreground'>
                          {row.completed} of {row.fte > 0 ? `${row.fte} FTE` : `${row.total} invited`}
                          {outstanding > 0 ? ` · ${outstanding} outstanding` : ''}
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='font-mono text-base tabular-nums'>{rate}%</div>
                        <Badge
                          variant={rate >= 70 ? 'success' : rate >= 50 ? 'info' : 'warning'}
                          size='sm'>
                          {rate >= 70 ? 'On track' : rate >= 50 ? 'Progressing' : 'Needs push'}
                        </Badge>
                      </div>
                    </div>
                    <Progress value={rate} />
                  </div>
                );
              })
            ) : (
              <div className='text-sm text-muted-foreground py-4'>
                {invited > 0
                  ? 'No location data available — add facilities to see per-location breakdown.'
                  : 'No invitations sent yet.'}
              </div>
            )}
          </CardContent>
        </Card>

        <div className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Campaign details</CardTitle>
            </CardHeader>
            <CardContent className='p-0'>
              <ol>
                {[
                  {
                    label: 'Campaign created',
                    value: prettyDate(campaign?.createdAt),
                    icon: Info,
                  },
                  {
                    label: 'Invitations sent',
                    value: invited > 0 ? `${invited} invitations` : 'Not yet sent',
                    icon: Mail,
                  },
                  {
                    label: 'Deadline',
                    value: prettyDate(deadlineRaw),
                    icon: Clock,
                  },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li key={i} className='flex gap-3 px-5 py-3 border-b border-border last:border-b-0'>
                      <div className='w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-accent flex-shrink-0'>
                        <Icon size={14} strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className='text-sm'>{item.value}</div>
                        <div className='text-xs text-muted-foreground'>{item.label}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>

          {isActive && (
            <Alert>
              <Info size={14} strokeWidth={1.5} />
              <AlertTitle>Auto-reminders are on</AlertTitle>
              <AlertDescription>
                We will nudge outstanding invitations 7 days and 2 days before{' '}
                {deadlineRaw ? 'the deadline' : 'closing'}.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
